import React, { useState } from 'react';
import { AlertTriangle, Lock, X } from 'lucide-react';
import { GateMissingItem } from '../../types/stageGate';

interface StageBlockedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBypassAsCEO?: (reason: string) => void; // Only shown when user.role === 'CEO'
  targetStage: string;
  leadName: string;
  missing: GateMissingItem[];
  isCEO: boolean;
}

export const StageBlockedModal: React.FC<StageBlockedModalProps> = ({
  isOpen,
  onClose,
  onBypassAsCEO,
  targetStage,
  leadName,
  missing,
  isCEO,
}) => {
  const [showBypassInput, setShowBypassInput] = useState(false);
  const [bypassReason, setBypassReason] = useState('');

  if (!isOpen) return null;

  const handleBypassConfirm = () => {
    if (!bypassReason.trim()) return;
    onBypassAsCEO?.(bypassReason.trim());
    setBypassReason('');
    setShowBypassInput(false);
  };

  const handleClose = () => {
    setShowBypassInput(false);
    setBypassReason('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <Lock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Stage Gate Blocked</h2>
              <p className="text-sm text-gray-500">Cannot advance to {targetStage}</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-700">
            <span className="font-medium">{leadName}</span> cannot advance to{' '}
            <span className="font-medium text-amber-700">{targetStage}</span> because the
            following required deliverables are missing:
          </p>

          <ul className="space-y-2">
            {missing.map((item) => (
              <li
                key={item.category}
                className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
                <div>
                  <p className="text-sm font-medium text-amber-900">{item.category}</p>
                  {item.description && (
                    <p className="text-xs text-amber-700 mt-0.5">{item.description}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <p className="text-xs text-gray-500">
            Add the required deliverables on the lead detail page, then try advancing the stage again.
          </p>

          {/* CEO bypass section */}
          {isCEO && !showBypassInput && (
            <div className="pt-2 border-t border-gray-200">
              <button
                onClick={() => setShowBypassInput(true)}
                className="text-sm text-red-600 hover:text-red-800 font-medium"
              >
                Override with Reason (CEO only)
              </button>
            </div>
          )}

          {isCEO && showBypassInput && (
            <div className="pt-2 border-t border-gray-200 space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Bypass Reason (required)
              </label>
              <textarea
                value={bypassReason}
                onChange={(e) => setBypassReason(e.target.value)}
                placeholder="Enter reason for bypassing the stage gate..."
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleBypassConfirm}
                  disabled={!bypassReason.trim()}
                  className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm Override
                </button>
                <button
                  onClick={() => setShowBypassInput(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Close and Add Deliverables
          </button>
        </div>
      </div>
    </div>
  );
};
